import React, { useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { Assignment } from '@mui/icons-material';
import { useToast as useRocketToast } from '@totalsoft/rocket-ui';
import RefreshPageToast from './components/RefreshPageToast';
import { useAuthorizations } from 'hooks';
import { copyToClipboard } from 'utils/functions';
import { ToastLogAnchor } from './components/ToastStyles';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';

export const useToast = () => {
  const addToast = useRocketToast();
  const refreshPageToastId = useRef();

  const authorizations = useAuthorizations([AuthorizationCodes.hasRightToViewLogs], null, null);

  const addRefreshPageToast = useCallback(
    (message, toastId) => {
      if (refreshPageToastId.current) return; //toast is already displayed

      const events = {
        onDismiss: () => {
          if (refreshPageToastId.current) {
            toast.dismiss(refreshPageToastId.current);
            refreshPageToastId.current = null;
          }
        }
      };
      refreshPageToastId.current = toastId;
      addToast(<RefreshPageToast message={message} events={events} />, 'info');
    },
    [addToast]
  );

  const fn = useCallback(
    (message, variant, { toastId, autoClose = true, ...rest }, correlationId, refreshPage = false) => {
      switch (variant) {
        case 'error': {
          const content = correlationId ? (
            <p>
              {message}{' '}
              <ToastLogAnchor href={`/logs/${correlationId}`} target='_blank' active={authorizations?.hasRightToViewLogs}>
                {correlationId}
              </ToastLogAnchor>
              {authorizations?.hasRightToViewLogs && (
                <Assignment fontSize={'inherit'} sx={{ ml: 1 }} onClick={copyToClipboard(correlationId)} />
              )}
            </p>
          ) : (
            message
          );

          addToast(content, 'error', { toastId: toastId, autoClose, ...rest });
          break;
        }
        case 'info': {
          if (refreshPage) {
            addRefreshPageToast(message, toastId);
          } else {
            addToast(message, 'info', { toastId: toastId, autoClose, ...rest });
          }
          break;
        }
        case 'success':
          addToast(message, 'success', { toastId: toastId, autoClose, ...rest });
          break;
        case 'warning':
          addToast(message, 'warning', { toastId: toastId, autoClose, ...rest });
          break;
        default:
          addToast(message, undefined, { toastId: toastId, autoClose, ...rest });
          break;
      }
    },
    [addToast, addRefreshPageToast, authorizations?.hasRightToViewLogs]
  );
  return fn;
};
